import {memo} from 'react';
import type {ReactNode} from 'react';
import Eyebrow from '@/components/atoms/Eyebrow';
import styles from './SectionHeader.module.scss';

interface SectionHeaderProps {
    /** A string becomes the standard Eyebrow; a node renders as-is. */
    eyebrow?: ReactNode;
    heading: ReactNode;
    /** Bold h3-styled line between the heading and the subheading. */
    boldSubheading?: ReactNode;
    subheading?: ReactNode;
    /** Subheading typography variant. Defaults to `subtitle1`. */
    subheadingVariant?: 'subtitle1' | 'body1';
    align?: 'center' | 'left';
    /** `wide`: the card-grid header — 1024px wide, 24px under the heading, 40px under the block. */
    layout?: 'standard' | 'wide';
    /** `large`: h1 at 35px, 48px from `sm` (instead of the section heading scale). */
    headingSize?: 'section' | 'large';
    className?: string;
    headingClassName?: string;
    subheadingClassName?: string;
}

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

/** Plain stand-in for Common/SectionHeader: eyebrow + h1-styled <h2> + optional subtitle1 subheading. */
const SectionHeader = ({
    eyebrow,
    heading,
    boldSubheading,
    subheading,
    subheadingVariant = 'subtitle1',
    align = 'center',
    layout = 'standard',
    headingSize = 'section',
    className,
    headingClassName,
    subheadingClassName
}: SectionHeaderProps) => (
    <div className={cx(styles.wrapper, layout === 'wide' && styles.wide, align === 'left' && styles.left, className)}>
        {typeof eyebrow === 'string' ? <Eyebrow text={eyebrow} /> : eyebrow}
        <h2 className={cx('font-headers', styles.heading, headingSize === 'large' && styles.large, headingClassName)}>{heading}</h2>
        {boldSubheading && <p className={`font-headers ${styles.boldSubheading}`}>{boldSubheading}</p>}
        {subheading !== undefined && subheading !== null && subheading !== '' && (
            <p className={cx('font-regular-text', styles[subheadingVariant], subheadingClassName)}>{subheading}</p>
        )}
    </div>
);

export default memo(SectionHeader);
